import { Link, useLocation } from 'react-router-dom';
import { useState } from 'react';
import { Menu, X, Sun, Moon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '@/context/ThemeContext';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { isDark, toggleTheme } = useTheme();
  const location = useLocation();

  const toggleMenu = () => setIsOpen(!isOpen);

  const navLinks = [
    { to: '/', label: 'Home', testId: 'nav-home' },
    { to: '/explore', label: 'Explore Projects', testId: 'nav-explore' },
    { to: '/submit-idea', label: 'Submit an Idea', testId: 'nav-submit-idea' },
    { to: '/community', label: 'Join our Community', testId: 'nav-community' },
    { to: '/contact', label: 'Contact Us', testId: 'nav-contact' },
  ];

  return (
    <motion.nav 
      className={`fixed top-0 left-0 right-0 z-50 backdrop-blur-md shadow-2xl border-b transition-colors duration-300 ${
        isDark 
          ? 'bg-gradient-to-r from-[#1a0b2e] via-[#2d1b4e] to-[#1a0b2e] border-purple-500/20' 
          : 'bg-white/95 border-gray-200'
      }`}
      data-testid="navbar"
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-3">
          {/* Logo and Tagline */}
          <Link to="/" className="flex items-center space-x-3 group" data-testid="navbar-logo">
            <motion.div
              className="relative flex-shrink-0"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <motion.img 
                src="/logo.jpg" 
                alt="Prodizzy Logo"
                className={`h-12 w-12 sm:h-14 sm:w-14 rounded-full object-cover border-2 shadow-lg transition-all duration-300 ${
                  isDark 
                    ? 'border-purple-400/50 shadow-purple-500/50 group-hover:border-purple-400 group-hover:shadow-purple-400/70' 
                    : 'border-purple-300 shadow-purple-300/50 group-hover:border-purple-500'
                }`}
                whileHover={{ 
                  boxShadow: isDark 
                    ? "0 0 30px rgba(168, 85, 247, 0.6), 0 0 60px rgba(168, 85, 247, 0.3)"
                    : "0 0 20px rgba(168, 85, 247, 0.4)"
                }}
              />
              <motion.div
                className="absolute inset-0 rounded-full bg-gradient-to-br from-purple-400/30 to-pink-400/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-sm"
                initial={false}
              />
            </motion.div>
            
            {/* Brand Name */}
            <motion.div 
              className="hidden sm:block"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
            >
              <span className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Prodizzy
              </span>
            </motion.div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1 lg:space-x-2">
            {navLinks.map((link, index) => (
              <motion.div
                key={link.to}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Link 
                  to={link.to} 
                  className={`relative px-3 lg:px-4 py-2 font-medium rounded-full text-sm lg:text-base transition-colors z-10 ${
                    isDark
                      ? location.pathname === link.to 
                        ? 'text-white' 
                        : 'text-gray-300 hover:!text-white'
                      : location.pathname === link.to 
                        ? 'text-purple-600' 
                        : 'text-gray-700 hover:!text-black'
                  }`}
                  data-testid={link.testId}
                >
                  {link.label}
                  {location.pathname === link.to && (
                    <motion.div
                      className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-purple-400 via-pink-400 to-purple-400 rounded-full"
                      layoutId="navbar-indicator"
                      transition={{ type: "spring", stiffness: 380, damping: 30 }}
                    />
                  )}
                  <motion.div
                    className={`absolute inset-0 rounded-full -z-10 ${
                      isDark ? 'hover:bg-white/5' : 'hover:bg-purple-50'
                    }`}
                    whileHover={{ scale: 1.05 }}
                  />
                </Link>
              </motion.div>
            ))}
            
            {/* Theme Toggle Button - Desktop */}
            {/* <motion.button
              onClick={toggleTheme}
              className={`p-2 rounded-full transition-all duration-300 ml-2 ${
                isDark 
                  ? 'bg-white/10 hover:bg-white/20 text-yellow-300' 
                  : 'bg-purple-100 hover:bg-purple-200 text-purple-600'
              }`}
              whileHover={{ scale: 1.1, rotate: 180 }}
              whileTap={{ scale: 0.9 }}
              title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            >
              <AnimatePresence mode="wait">
                {isDark ? (
                  <motion.div
                    key="sun"
                    initial={{ rotate: -180, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: 180, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Sun size={20} />
                  </motion.div>
                ) : (
                  <motion.div
                    key="moon"
                    initial={{ rotate: 180, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: -180, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Moon size={20} />
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.button> */}
          </div>

          {/* Mobile Controls */}
          <div className="md:hidden flex items-center space-x-2">
            {/* Theme Toggle Button - Mobile */}
            {/* <motion.button
              onClick={toggleTheme}
              className={`p-2 rounded-full transition-all duration-300 ${
                isDark 
                  ? 'bg-white/10 hover:bg-white/20 text-yellow-300' 
                  : 'bg-purple-100 hover:bg-purple-200 text-purple-600'
              }`}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            >
              <AnimatePresence mode="wait">
                {isDark ? (
                  <motion.div
                    key="sun"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <Sun size={20} />
                  </motion.div>
                ) : (
                  <motion.div
                    key="moon"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <Moon size={20} />
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.button> */}

            {/* Mobile menu button */}
            <motion.button
              onClick={toggleMenu}
              className={`p-2 rounded-full transition-colors ${
                isDark 
                  ? 'text-gray-300 hover:text-white hover:bg-white/10' 
                  : 'text-gray-700 hover:text-purple-600 hover:bg-purple-50'
              }`}
              data-testid="mobile-menu-button"
              whileTap={{ scale: 0.9 }}
            >
            <AnimatePresence mode="wait">
              {isOpen ? (
                <motion.div
                  key="close"
                  initial={{ rotate: -90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: 90, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <X size={24} />
                </motion.div>
              ) : (
                <motion.div
                  key="menu"
                  initial={{ rotate: 90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: -90, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <Menu size={24} />
                </motion.div>
              )}
            </AnimatePresence>
          </motion.button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isOpen && (
            <motion.div 
              className={`md:hidden pb-4 rounded-b-2xl ${
                isDark ? 'bg-[#1a0b2e]/95' : 'bg-white'
              }`}
              data-testid="mobile-menu"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              {/* Tagline removed from mobile menu */}
              
              {navLinks.map((link, index) => (
                <motion.div
                  key={link.to}
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Link
                    to={link.to}
                    className={`block py-3 px-4 rounded-lg transition-colors font-medium ${
                      isDark
                        ? location.pathname === link.to
                          ? 'text-white bg-purple-600/30'
                          : 'text-gray-300 hover:text-white hover:bg-white/10'
                        : location.pathname === link.to
                          ? 'text-purple-600 bg-purple-50'
                          : 'text-gray-700 hover:text-purple-600 hover:bg-purple-50'
                    }`}
                    onClick={toggleMenu}
                    data-testid={`mobile-${link.testId}`}
                  >
                    {link.label}
                  </Link>
                </motion.div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.nav>
  );
};

export default Navbar;
